import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, ChevronRight } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("__root__");

  const load = async () => {
    const { data } = await api.get("/categories", { params: { include_hidden: true } });
    setCats(data);
  };
  useEffect(() => { load(); }, []);

  const roots = cats.filter((c) => !c.parent_id);
  const subsOf = (id) => cats.filter((c) => c.parent_id === id);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const payload = { name: name.trim(), order: cats.length + 1 };
      if (parentId && parentId !== "__root__") payload.parent_id = parentId;
      await api.post("/admin/categories", payload);
      setName("");
      toast.success("Categoria creata");
      load();
    } catch (e) {
      toast.error("Errore", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  const update = async (cat, updates) => {
    await api.put(`/admin/categories/${cat.id}`, { ...cat, ...updates });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare la categoria?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Categoria eliminata");
    load();
  };

  const move = async (siblings, idx, dir) => {
    const arr = [...siblings];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    const payload = arr.map((c, i) => ({ id: c.id, order: i + 1 }));
    await api.post("/admin/categories/reorder", payload);
    load();
  };

  const CatRow = ({ c, siblings, idx, isSub }) => (
    <div data-testid={`cat-row-${c.slug}`} className={`bg-white border border-border rounded-2xl p-3 flex items-center gap-3 ${isSub ? "ml-6" : ""}`}>
      {isSub && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
      <div className="flex flex-col gap-0.5">
        <button data-testid={`up-${c.slug}`} onClick={() => move(siblings, idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-brand-blue disabled:opacity-30">
          <ArrowUp className="w-4 h-4" />
        </button>
        <button data-testid={`down-${c.slug}`} onClick={() => move(siblings, idx, 1)} disabled={idx === siblings.length - 1} className="text-slate-400 hover:text-brand-blue disabled:opacity-30">
          <ArrowDown className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <Input
          data-testid={`cat-name-${c.slug}`}
          defaultValue={c.name}
          onBlur={(e) => e.target.value !== c.name && update(c, { name: e.target.value })}
          className={`border-none px-2 focus-visible:ring-1 rounded-lg ${isSub ? "text-sm" : "font-heading font-bold"}`}
        />
        <div className="text-xs text-slate-400 px-2">/{c.slug}</div>
      </div>
      <button
        data-testid={`toggle-${c.slug}`}
        onClick={() => update(c, { hidden: !c.hidden })}
        className={`p-2 rounded-lg ${c.hidden ? "text-slate-400 bg-slate-100" : "text-brand-blue bg-blue-50"}`}
      >
        {c.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
      <button data-testid={`del-cat-${c.slug}`} onClick={() => del(c.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <AdminLayout title="Categorie" back="/admin">
      <div className="bg-white border border-border rounded-2xl p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
          <Input
            data-testid="new-cat-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome nuova categoria"
            className="rounded-xl h-11 sm:col-span-3"
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
          <Select value={parentId} onValueChange={setParentId}>
            <SelectTrigger data-testid="new-cat-parent" className="rounded-xl h-11 sm:col-span-2">
              <SelectValue placeholder="Categoria principale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__root__">Nessuna (nuova principale)</SelectItem>
              {roots.map((r) => <SelectItem key={r.id} value={r.id}>Sotto: {r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="add-cat-btn" onClick={create} className="rounded-xl bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {roots.map((r, ri) => {
          const subs = subsOf(r.id);
          return (
            <div key={r.id} className="space-y-2">
              <CatRow c={r} siblings={roots} idx={ri} isSub={false} />
              {subs.length > 0 && (
                <div className="space-y-2">
                  {subs.map((s, si) => (
                    <CatRow key={s.id} c={s} siblings={subs} idx={si} isSub={true} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
